import { createColumnHelper } from '@tanstack/react-table';
import { HardDrive } from 'lucide-react';

import { createStore } from '@/react/kubernetes/datatables/default-kube-datatable-store';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';
import { Badge } from '@@/Badge';

import { K8sGPUNode, K8sGPUNodeStatus } from './queries/useGpuQuery';

const columnHelper = createColumnHelper<K8sGPUNode>();

const storageKey = 'k8sGpuNodesDatatable';
const settingsStore = createStore(storageKey);

const columns = [
  columnHelper.accessor('Name', {
    header: 'Node',
    id: 'name',
  }),
  columnHelper.accessor(
    (row) =>
      Object.entries(row.Capacity)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', '),
    {
      header: 'Capacity',
      id: 'capacity',
    }
  ),
  columnHelper.accessor(
    (row) =>
      Object.entries(row.Allocatable)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', '),
    {
      header: 'Allocatable',
      id: 'allocatable',
    }
  ),
  columnHelper.accessor(
    (row) =>
      Object.entries(row.Allocated)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ') || '-',
    {
      header: 'Allocated',
      id: 'allocated',
    }
  ),
  columnHelper.accessor('Status', {
    header: 'Status',
    id: 'status',
    cell: ({ getValue, row }) => {
      const status = getValue();
      return (
        <div className="flex items-center gap-x-1">
          <Badge type={statusBadgeType(status)}>{status}</Badge>
          {row.original.StatusReason && (
            <span className="text-muted text-xs">
              ({row.original.StatusReason})
            </span>
          )}
        </div>
      );
    },
  }),
];

interface Props {
  nodes: K8sGPUNode[];
  isLoading: boolean;
}

export function GpuNodesDatatable({ nodes, isLoading }: Props) {
  const tableState = useTableState(settingsStore, storageKey);

  return (
    <Datatable<K8sGPUNode>
      disableSelect
      dataset={nodes}
      columns={columns}
      settingsManager={tableState}
      isLoading={isLoading}
      title="GPU Nodes"
      titleIcon={HardDrive}
      getRowId={(row) => row.Name}
      data-cy="k8s-gpu-nodes-datatable"
    />
  );
}

function statusBadgeType(
  status: K8sGPUNodeStatus
): 'success' | 'warn' | 'muted' {
  switch (status) {
    case 'Ready':
      return 'success';
    case 'Degraded':
      return 'warn';
    case 'Not Available':
    default:
      return 'muted';
  }
}
